import { parseTeamsData } from "../utils/parseTeamsData";
import type { TeamsJsonInput } from "../utils/parseTeamsData";
import { getTeamsMap, setTeamsMap } from "./useCuiConfig";

/**
 * Default endpoint requires these headers for access:
 * - X-Platform: one of (native-ios, cordova-desktop, etc.)
 * - X-App-Version: any value
 * - X-Unique-Device-Id: any value
 */
const DEFAULT_ENDPOINT = "https://api.americanwagering.com/caesars-ui-assets/json/teams.json";
const DEFAULT_POLL_INTERVAL = 86400000; // 24 hours
const MIN_POLL_INTERVAL = 5000; // 5 seconds minimum
const FETCH_TIMEOUT = 30000; // 30 seconds

const DEFAULT_ENDPOINT_HEADERS: HeadersInit = {
  "X-Platform": "cordova-desktop",
  "X-App-Version": "1.0.0",
  "X-Unique-Device-Id": "cui-fetch"
};

let lastETag: string | null = null;
let lastModified: string | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

export type FetchTeamsOptions = {
  endpoint?: string;
  forceUpdate?: boolean;
  polling?: boolean;
  interval?: number;
};

export type FetchTeamsResult = {
  success: boolean;
  updated: boolean;
  version?: number;
  error?: string;
  stop?: () => void;
};

/**
 * Validates fetch options
 */
function validateOptions(options: unknown): options is FetchTeamsOptions {
  if (!options || typeof options !== "object") return true; // Allow empty options, will use defaults
  return true;
}

/**
 * Validates teams JSON structure
 */
function isValidTeamsJson(data: unknown): data is TeamsJsonInput {
  if (!data || typeof data !== "object") return false;
  const json = data as Record<string, unknown>;
  return typeof json.teams === "object" && json.teams !== null;
}

/**
 * Fetches with timeout
 */
async function fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetches teams.json from endpoint using conditional requests (ETag/Last-Modified).
 * Parses and updates global CUI.teamsMap only if modified.
 */
export async function fetchTeams(options?: FetchTeamsOptions): Promise<FetchTeamsResult> {
  const opts = options ?? {};
  const { endpoint = DEFAULT_ENDPOINT, forceUpdate = false, polling = false, interval = DEFAULT_POLL_INTERVAL } = opts;
  const safeInterval = Math.max(interval, MIN_POLL_INTERVAL);

  const result = await doFetch(endpoint, forceUpdate);

  if (polling && result.success) {
    stopTeamsPolling(); // Clear any existing timer
    pollTimer = setInterval(async () => { 
      const pollResult = await doFetch(endpoint, false);
      console.log("[fetchTeams poll]", pollResult);
    }, safeInterval);
    result.stop = stopTeamsPolling;
  }

  return result;
}

async function doFetch(endpoint: string, forceUpdate: boolean): Promise<FetchTeamsResult> {
  try {
    const isDefaultEndpoint = endpoint === DEFAULT_ENDPOINT;
    const headers: HeadersInit = isDefaultEndpoint ? { ...DEFAULT_ENDPOINT_HEADERS } : {};
    if (!forceUpdate) {
      if (lastETag) headers["If-None-Match"] = lastETag;
      if (lastModified) headers["If-Modified-Since"] = lastModified;
    }

    let response: Response;
    try {
      response = await fetchWithTimeout(endpoint, { headers }, FETCH_TIMEOUT);
    } catch (err) {
      const message = err instanceof Error && err.name === "AbortError" 
        ? "Request timed out" 
        : "Network error";
      return { success: false, updated: false, error: message };
    }

    if (response.status === 304) {
      return { success: true, updated: false, version: getTeamsMap()?.version ?? 0 };
    }

    if (!response.ok) {
      return { success: false, updated: false, error: `HTTP ${response.status}: ${response.statusText}` };
    }

    // Store headers for next conditional request
    lastETag = response.headers.get("ETag");
    lastModified = response.headers.get("Last-Modified");

    let teamsJson: unknown;
    try {
      teamsJson = await response.json();
    } catch {
      return { success: false, updated: false, error: "Invalid JSON response" };
    }

    if (!isValidTeamsJson(teamsJson)) {
      return { success: false, updated: false, error: "Invalid teams data structure" };
    }

    const newVersion = teamsJson.version ?? 1;
    const currentVersion = getTeamsMap()?.version ?? 0;

    if (!forceUpdate && newVersion <= currentVersion) {
      return { success: true, updated: false, version: currentVersion };
    }

    let parsedTeamsMap;
    try {
      parsedTeamsMap = parseTeamsData(teamsJson);
    } catch {
      return { success: false, updated: false, error: "Failed to parse teams data" };
    }

    setTeamsMap(parsedTeamsMap);
    return { success: true, updated: true, version: newVersion };
  } catch (error) {
    return { 
      success: false, 
      updated: false, 
      error: error instanceof Error ? error.message : "Unknown error" 
    };
  }
}

/** Stops any active polling */
export function stopTeamsPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/** Gets current teams map version, or 0 if not loaded */
export function getTeamsMapVersion(): number {
  return getTeamsMap()?.version ?? 0;
}
